import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import { Badge, Button, EmptyState, Skeleton } from '../../../ui';
import { apiErrorMessage } from '../../../utils/apiError';
import { useAgentProposals } from '../hooks/useAgentProposals';
import { STATUS_LABELS, STATUS_TONES, actionLabel, agentLabel, formatDateTime } from '../labels';
import type { AgentName, ProposalStatus } from '../services/agent-proposals.service';

const selectClass =
  'rounded-md border border-border bg-surface px-3 py-2 text-sm ' +
  'focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent';

const STATUS_OPTIONS: ProposalStatus[] = [
  'PENDING',
  'EXECUTED',
  'FAILED',
  'REJECTED',
  'EXPIRED',
  'APPROVED',
];
const AGENT_OPTIONS: AgentName[] = ['nami', 'brook', 'zoro', 'luffy'];

export function AprovacoesPage() {
  const [status, setStatus] = useState<ProposalStatus | ''>('PENDING');
  const [agent, setAgent] = useState<AgentName | ''>('');

  const query = useAgentProposals({
    status: status || undefined,
    agent: agent || undefined,
    limit: 100,
  });
  const items = query.data?.items ?? [];

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h1 className="font-serif text-2xl font-semibold text-ink-primary">Aprovações</h1>
        <p className="text-sm text-ink-secondary">
          Propostas da tripulação. Nada é gravado no CRM antes da sua aprovação.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="filter-proposal-status"
            className="text-xs font-medium text-ink-secondary"
          >
            Status
          </label>
          <select
            id="filter-proposal-status"
            className={selectClass}
            value={status}
            onChange={(e) => setStatus(e.target.value as ProposalStatus | '')}
          >
            <option value="">Todos</option>
            {STATUS_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {STATUS_LABELS[value]}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="filter-proposal-agent" className="text-xs font-medium text-ink-secondary">
            Agente
          </label>
          <select
            id="filter-proposal-agent"
            className={selectClass}
            value={agent}
            onChange={(e) => setAgent(e.target.value as AgentName | '')}
          >
            <option value="">Todos</option>
            {AGENT_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {agentLabel(value)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {query.isPending && (
        <div className="flex flex-col gap-3">
          <Skeleton height={96} />
          <Skeleton height={96} />
        </div>
      )}

      {query.isError && (
        <EmptyState
          title="Não foi possível carregar as propostas"
          body={apiErrorMessage(query.error, 'Erro inesperado ao carregar as propostas')}
          action={<Button onClick={() => void query.refetch()}>Tentar novamente</Button>}
        />
      )}

      {query.isSuccess && items.length === 0 && (
        <EmptyState
          illustration={<Inbox className="w-10 h-10" />}
          title="Nenhuma proposta"
          body={
            status === 'PENDING'
              ? 'Nenhuma proposta esperando a sua decisão.'
              : 'Troque os filtros para ver outras propostas.'
          }
        />
      )}

      {items.length > 0 && (
        <ul className="flex flex-col gap-3">
          {items.map((p) => (
            <li key={p.id}>
              <Link
                to={`/aprovacoes/${p.id}`}
                className="block rounded-lg border border-border bg-surface p-4 hover:bg-subtle"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-ink-tertiary">
                    #{p.id} · {agentLabel(p.agent)} · {actionLabel(p.actionType)}
                  </span>
                  <Badge tone={STATUS_TONES[p.status]}>{STATUS_LABELS[p.status]}</Badge>
                </div>
                <p className="mt-1 font-medium text-ink-primary">{p.preview.title}</p>
                <p className="mt-1 line-clamp-2 text-sm text-ink-secondary">{p.summary}</p>
                <p className="mt-2 text-xs text-ink-tertiary">
                  Criada em {formatDateTime(p.createdAt)} · vence em {formatDateTime(p.expiresAt)}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
